import { Fragment } from 'react'
import './index.scss'
import arch from '../../assets/images/arch.png'
import cpp from '../../assets/images/cpp.png'
import java from '../../assets/images/java.png'
import react from '../../assets/images/react.png'
import js from '../../assets/images/js.png'
import pyhton from '../../assets/images/python-logo-only.png'
import raytracer from '../../assets/images/ray_tracer.png'
import louhaus from '../../assets/images/louhaus.png'
import treemap from '../../assets/images/treemap.png'
import kristenTransporte from '../../assets/images/kristen-transporte.png'
import spenglereiHolderied from '../../assets/images/spenglerei-holderied.png'
import augustusImage from '../../assets/images/image_augustus.png'
import berlinTimeline from '../../assets/images/berlin-timeline.png'
import lastFreeShell from '../../assets/images/last-free-shell.png'
import serverInfrastructure from '../../assets/images/server-infrastructure.svg'
import louhausWebP from '../../assets/images/webp/louhaus.webp'
import kristenTransporteWebP from '../../assets/images/webp/kristen-transporte.webp'
import spenglereiHolderiedWebP from '../../assets/images/webp/spenglerei-holderied.webp'
import augustusImageWebP from '../../assets/images/webp/image_augustus.webp'
import berlinTimelineWebP from '../../assets/images/webp/berlin-timeline.webp'
import lastFreeShellWebP from '../../assets/images/webp/last-free-shell.webp'
import raytracerWebP from '../../assets/images/webp/ray_tracer.webp'
import BackgroundWaves from '../BackgroundWaves'
import OptimizedWebsiteImage from '../OptimizedWebsiteImage'
import { Trans, useTranslation } from 'react-i18next';

// Each card `id` is the prefix of its `<id>_title` and `<id>_desc` translation keys.
const WEB_PROJECTS = [
    {
        id: 'kristen_transporte',
        href: 'https://www.kristen-transporte.de/',
        image: kristenTransporte,
        webpImage: kristenTransporteWebP,
        buttonKey: 'visit_website',
    },
    {
        id: 'spenglerei_holderied',
        href: 'https://spenglerei-holderied.de/',
        image: spenglereiHolderied,
        webpImage: spenglereiHolderiedWebP,
        buttonKey: 'visit_website',
    },
    {
        id: 'louhaus_band',
        href: 'https://www.louhausband.de/',
        image: louhaus,
        webpImage: louhausWebP,
        buttonKey: 'visit_website',
    },
    {
        id: 'augustus_timeline',
        href: '/augustus',
        image: augustusImage,
        webpImage: augustusImageWebP,
        buttonKey: 'visit_website',
    },
    {
        id: 'berlin_timeline',
        href: '/berlin',
        image: berlinTimeline,
        webpImage: berlinTimelineWebP,
        buttonKey: 'visit_website',
    },
]

const PROGRAMMING_PROJECTS = [
    {
        id: 'last_free_shell',
        image: lastFreeShell,
        webpImage: lastFreeShellWebP,
        badgeKey: 'coming_soon',
    },
    {
        id: 'server',
        image: serverInfrastructure,
        imageClassName: 'diagram',
    },
    {
        id: 'pathtracer',
        href: 'https://github.com/VerIPt/path-tracer',
        image: raytracer,
        webpImage: raytracerWebP,
        buttonKey: 'view_github',
    },
    {
        id: 'treemap',
        href: 'https://github.com/VerIPt/treemap',
        image: treemap,
        buttonKey: 'view_github',
    },
]

// Translation keys of the skill cards in display order. Each key has a
// matching `<key>_text` entry for the card body.
const SKILLS = [
    'skill_hardware',
    'skill_automation',
    'skill_infra',
    'skill_electrical',
    'skill_languages',
    'skill_data',
    'skill_web',
]

/**
 * Portfolio card with title, description and image. Cards with an `href`
 * link the image and show a button; cards without one can show a badge.
 */
const ProjectCard = ({ id, href, image, webpImage, imageClassName, buttonKey, badgeKey }) => {
    const { t } = useTranslation();
    const title = t(`${id}_title`);
    const picture = webpImage
        ? <OptimizedWebsiteImage className={imageClassName} src={image} webpSrc={webpImage} alt={title} />
        : <img className={imageClassName} src={image} alt={title} />;

    return (
        <div className={href ? 'project' : 'project project--static'}>
            <h3>{title}</h3>
            <p className='project-description'>{t(`${id}_desc`)}</p>
            {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer">
                    {picture}
                    <button className='view-project-btn'>{t(buttonKey)}</button>
                </a>
            ) : picture}
            {badgeKey && <span className='project-badge'>{t(badgeKey)}</span>}
        </div>
    );
};

/**
 * Portfolio page with web projects, programming projects and skills.
 */
const Portfolio = () => {
    const { t } = useTranslation();

    return (
        <div className="container" >
            <BackgroundWaves />
            <div className="text-zone1">
                <h1>
                    <Trans i18nKey='portfolio' components={{ 1: <span /> }} />
                </h1>

                <div className="websites-section">
                    <h2>{t('websites_section')}</h2>
                    <p>{t('websites_description')}</p>

                    <div className='portfolio-websites'>
                        {WEB_PROJECTS.map((project) => (
                            <ProjectCard key={project.id} {...project} />
                        ))}
                    </div>
                </div>

                <div className="section-divider"></div>

                <div className="projects-section">
                    <h2>{t('projects_section')}</h2>
                    <p>{t('projects_description')}</p>

                    <div className='portfolio-projects'>
                        {PROGRAMMING_PROJECTS.map((project) => (
                            <ProjectCard key={project.id} {...project} />
                        ))}
                    </div>
                </div>

                <div className="section-divider"></div>

                <h1>
                    <Trans i18nKey='skills' components={{ 1: <span /> }} />
                </h1>

                {SKILLS.map((key, index) => (
                    <Fragment key={key}>
                        {index > 0 && <div className="skill-divider"></div>}
                        <div className="skill-section">
                            <h3>
                                <Trans i18nKey={key} components={{ 1: <span /> }} />
                            </h3>
                            <p>{t(`${key}_text`)}</p>
                        </div>
                    </Fragment>
                ))}
            </div>
            <div className='stage-cube-cont'>
                <div className='cubespinner'>
                    <div className='face1'>
                        <img src={pyhton} alt="" />
                    </div>
                    <div className='face2'>
                        <img src={cpp} alt="" />
                    </div>
                    <div className='face3'>
                        <img src={arch} alt="" />
                    </div>
                    <div className='face4'>
                        <img src={react} alt="" />
                    </div>
                    <div className='face5'>
                        <img src={java} alt="" />
                    </div>
                    <div className='face6'>
                        <img src={js} alt="" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Portfolio;
